import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useContext } from 'react';

import { STALE_TIME } from '~/domains/contest/constants/query';
import { getContests } from '~/services/contests/contests.service.api';
import { useLocationKey } from '~/hooks/useLocationKey';
import { UserContext } from '~/components/providers/UserProvider';
import { PUSHER_SEARCH_ENDPOINT } from '~/domains/pusher/constants';
import { usePusherFetch } from '~/domains/pusher/hooks/usePusherFetch';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';

const INFINITE_SCROLL_PAGE_SIZE = 10;

type UseContestSectionInfiniteProps = {
  contestIds: string[];
  sectionTitle: string;
  includeFull?: boolean;
  enabled?: boolean;
};

export const useContestSectionInfinite = ({
  contestIds,
  sectionTitle,
  includeFull = false,
  enabled = true,
}: UseContestSectionInfiniteProps) => {
  const { user } = useContext(UserContext);
  const { locationKey, isLocationReady } = useLocationKey();
  const { fetchRows, isReady: isPusherReady } = usePusherFetch<Contest>({
    endpoint: PUSHER_SEARCH_ENDPOINT,
  });

  const data = useInfiniteQuery({
    queryKey: [
      'contest-section-infinite',
      sectionTitle,
      contestIds.join(','),
      user?.id,
      locationKey,
      includeFull,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const startIndex = pageParam;
      const endIndex = Math.min(startIndex + INFINITE_SCROLL_PAGE_SIZE, contestIds.length);
      const pageContestIds = contestIds.slice(startIndex, endIndex);

      if (pageContestIds.length === 0) {
        return {
          data: [],
          offset: startIndex,
          limit: INFINITE_SCROLL_PAGE_SIZE,
          total: contestIds.length,
        };
      }

      const payload = {
        filter: { contestIds: pageContestIds },
        limit: pageContestIds.length,
        offset: 0, // Always 0 since we're filtering by specific IDs
        includeFull,
      };

      const rows = await fetchRows((headers) => getContests(payload, headers));

      // Maintain the order based on original contestIds array
      const orderedContests = pageContestIds
        .map((id) => rows.find((contest) => contest.id === id))
        .filter((contest): contest is Contest => !!contest);

      return {
        data: orderedContests,
        offset: startIndex,
        limit: INFINITE_SCROLL_PAGE_SIZE,
        total: contestIds.length,
      };
    },
    enabled: enabled && isLocationReady && isPusherReady && contestIds.length > 0,
    staleTime: STALE_TIME,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.offset + INFINITE_SCROLL_PAGE_SIZE;
      if (nextPage >= contestIds.length) {
        return undefined;
      }
      return nextPage;
    },
  });

  // Flatten all pages into a single array
  const contests = useMemo(() => data.data?.pages.flatMap((page) => page.data) ?? [], [data.data]);

  const hasNextPage = useMemo(() => {
    if (!data.data?.pages.length) return contestIds.length > 0;
    const loadedCount = contests.length;
    return loadedCount < contestIds.length;
  }, [contests.length, contestIds.length, data.data?.pages.length]);

  return {
    ...data,
    contests,
    hasNextPage,
    itemsTotal: contestIds.length,
    loadedCount: contests.length,
  };
};
